import { useState } from 'react'

import { Box, Divider, Link, Text } from '@factorialco/deprecated-design-system'
import {
  FinanceValueObjectsJournalEntryTypeEnum,
  FinanceValueObjectsLedgerAccountBalanceTypeEnum,
  JournalLineReconciliationFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { PartialPendingJournalModal } from './PartialPendingJournalModal'
import { ReconciliationJournalLinesDataTable } from './ReconciliationJournalLinesDatatable'
import { ReconciliationSuggestions } from './ReconciliationSuggestions'
import { ReconciliationSummary } from './ReconciliationSummary'
import { SplitReconciledAmountsModal } from './SplitReconciledAmountsModal'
import { TagLabelComponent } from './TagLabelComponent'
import {
  ReconciliationItem,
  ReconciliationSource,
  ReconciliationStatusEnum,
  SourceReconciliationType,
} from './types'
import { useReconciliationForm } from './useReconciliationForm'

interface Props {
  sourceType: SourceReconciliationType
  journalLine?: JournalLineReconciliationFragment
  excludingJournalEntryTypes?: FinanceValueObjectsJournalEntryTypeEnum[]
  allowedJournalEntryTypes?: FinanceValueObjectsJournalEntryTypeEnum[]
  onlyJournalLinesFromSuspenseLedgerAccount?: boolean
  onlyReconcilableJournalLines?: boolean
  loading: boolean
  currency: string
  reconcilableJournalLineTitle: string
  summaryTitle: string
  summarySubtitle: string
  onJournalLineFullyReconciled: () => void
}

export const ReconciliationForm = ({
  sourceType,
  journalLine,
  excludingJournalEntryTypes,
  allowedJournalEntryTypes,
  onlyJournalLinesFromSuspenseLedgerAccount,
  onlyReconcilableJournalLines,
  loading,
  currency,
  reconcilableJournalLineTitle,
  summaryTitle,
  summarySubtitle,
  onJournalLineFullyReconciled,
}: Props) => {
  const { locale } = useCurrentSession().access
  const [source, setSource] = useState<ReconciliationSource | undefined>(undefined)
  const [sourceReconciliations, setSourceReconciliations] = useState<ReconciliationItem[]>([])
  const [showPartialPendingBankJournalModal, setShowPartialPendingBankJournalModal] =
    useState<boolean>(false)
  const [showPartialPendingNotBankJournalModal, setShowPartialPendingNotBankJournalModal] =
    useState<boolean>(false)
  const [showSplitAmountsModal, setShowSplitAmountsModal] = useState<boolean>(false)
  const [showSuggestions, setShowSuggestions] = useState<boolean>(true)

  useUnsafeEffect(() => {
    if (!journalLine) {
      setSource(undefined)
      setSourceReconciliations([])
    } else {
      const reconciliationStatus = journalLine.fullyReconciledAt
        ? ReconciliationStatusEnum.Full
        : journalLine.reconciliations.length
        ? ReconciliationStatusEnum.Partial
        : ReconciliationStatusEnum.None

      setSource({
        id: journalLine.id,
        description: journalLine.journalEntry.description || '',
        creditCentsToReconcile: journalLine.creditAmountCents,
        debitCentsToReconcile: journalLine.debitAmountCents,
        date: journalLine.journalEntry.publishedAt,
        reconciliationStatus: reconciliationStatus,
        currency,
        type: sourceType,
      })

      const reconciliations = (journalLine.reconciliations || [])
        .slice()
        .sort((a, b) => {
          return a.id - b.id
        })
        .filter((r) => r.reconciliationType !== 'skipped')
        .map((r) => {
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          const reconcilableJournalLine = r.reconcilableJournalLine!

          const creditCentsToReconcile =
            reconcilableJournalLine.creditAmountCents > 0
              ? r.reconcilableReconciledAmountCents ?? 0
              : 0
          const debitCentsToReconciline =
            reconcilableJournalLine.debitAmountCents > 0
              ? r.reconcilableReconciledAmountCents ?? 0
              : 0

          return {
            reconciliationId: r.id,
            id: reconcilableJournalLine.id,
            description: reconcilableJournalLine.journalEntry.description || '',
            creditCentsToReconcile: creditCentsToReconcile,
            debitCentsToReconcile: debitCentsToReconciline,
            currency,
            date: reconcilableJournalLine.journalEntry.publishedAt || '',
          }
        })

      const reconcilableReconciliations = (journalLine.reconcilableReconciliations || [])
        .slice()
        .sort((a, b) => {
          return a.id - b.id
        })
        .filter((r) => r.reconciliationType !== 'skipped')
        .map((r) => {
          const creditCentsToReconcile =
            r.journalLine.creditAmountCents > 0 ? r.reconcilableReconciledAmountCents ?? 0 : 0
          const debitCentsToReconciline =
            r.journalLine.debitAmountCents > 0 ? r.reconcilableReconciledAmountCents ?? 0 : 0

          return {
            reconciliationId: r.id,
            id: r.journalLine.id,
            description: r.journalLine.journalEntry.description || '',
            creditCentsToReconcile: creditCentsToReconcile,
            debitCentsToReconcile: debitCentsToReconciline,
            currency,
            date: r.journalLine.journalEntry.publishedAt || '',
          }
        })

      setSourceReconciliations(reconciliations.concat(reconcilableReconciliations))
    }
  }, [
    journalLine,
    journalLine?.reconciliations,
    journalLine?.reconcilableReconciliations,
    currency,
    sourceType,
  ])

  const handleOnJournalLineFullyReconciled = () => {
    setSource(undefined)
    onJournalLineFullyReconciled()
  }

  const balaceTypesToReconcile: FinanceValueObjectsLedgerAccountBalanceTypeEnum[] = source
    ? source.creditCentsToReconcile > 0
      ? ['debit']
      : ['credit']
    : ['credit', 'debit']

  const {
    handleReconcile,
    handleSubmitPartialModal,
    handleSubmitSplitAmountsModal,
    addReconciliation,
    removeReconciliation,
    isSelectedReconciliation,
    dirtyForm,
    selectedReconciliations,
    newReconciliations,
    initialAmountPendingToBeReconciled,
  } = useReconciliationForm({
    source,
    sourceReconciliations,
    onJournalLineFullyReconciled: handleOnJournalLineFullyReconciled,
    setShowPartialPendingBankJournalModal,
    setShowPartialPendingNotBankJournalModal,
    setShowSplitAmountsModal,
  })

  const suggestions =
    sourceType === SourceReconciliationType.BankAccount
      ? journalLine?.reconciliationSuggestions
      : journalLine?.reconcilableReconciliationSuggestions

  const canShowSuggestions =
    journalLine &&
    !!(suggestions && suggestions.length > 0) &&
    !journalLine.fullyReconciledAt &&
    !loading

  return (
    <Box style={{ minWidth: '500px' }}>
      <Box flexDirection="column" gap="s16" overflowY="auto" style={{ scrollbarGutter: 'auto' }}>
        {sourceType === SourceReconciliationType.FinancialDocument && (
          <Box paddingX="s16" flexDirection="column" gap="s4">
            <Text.Normal weight="semibold">
              {i18n.t('finance.reconciliations.financial_document_journal_lines.title')}
            </Text.Normal>
            <Text.Normal weight="normal">
              {i18n.t('finance.reconciliations.financial_document_journal_lines.description')}
            </Text.Normal>
          </Box>
        )}

        <Box paddingX="s16">
          <ReconciliationSummary
            source={source}
            reconciliations={selectedReconciliations}
            onRemove={removeReconciliation}
            loading={loading}
            title={summaryTitle}
            subtitle={summarySubtitle}
          />
        </Box>

        {sourceType !== SourceReconciliationType.FinancialDocument && <Divider />}

        {canShowSuggestions && journalLine && showSuggestions && (
          <Box paddingX="s16">
            <ReconciliationSuggestions
              journalLine={journalLine}
              sourceType={sourceType}
              currency={currency}
              locale={locale}
              onHideSuggestions={() => setShowSuggestions(false)}
            />
          </Box>
        )}

        {(!canShowSuggestions || !showSuggestions) && (
          <>
            <Box
              paddingX="s16"
              gap="s12"
              style={{ minHeight: '300px', maxHeight: 'calc(100vh - 462px)' }}
              overflowY="auto"
            >
              <Box
                flexDirection="row"
                width="full"
                alignItems="center"
                justifyContent="spaceBetween"
              >
                <Text.Normal weight="semibold">{reconcilableJournalLineTitle}</Text.Normal>
                {canShowSuggestions && (
                  <Link.Touchable onClick={() => setShowSuggestions(true)}>
                    {i18n.t('finance.reconciliations.suggestions.show')}
                  </Link.Touchable>
                )}
              </Box>
              <ReconciliationJournalLinesDataTable
                onSelect={addReconciliation}
                onRemove={removeReconciliation}
                isSelected={isSelectedReconciliation}
                disabled={!source}
                currency={currency}
                balanceType={balaceTypesToReconcile}
                excludingJournalEntryTypes={excludingJournalEntryTypes}
                allowedJournalEntryTypes={allowedJournalEntryTypes}
                onlyJournalLinesFromSuspenseLedgerAccount={
                  onlyJournalLinesFromSuspenseLedgerAccount
                }
                onlyReconcilableJournalLines={onlyReconcilableJournalLines}
              />
            </Box>
            <Box paddingX="s16">
              <TagLabelComponent source={source} dirty={dirtyForm} onReconcile={handleReconcile} />
            </Box>
          </>
        )}
      </Box>
      {source && showSplitAmountsModal && (
        <SplitReconciledAmountsModal
          onClose={() => setShowSplitAmountsModal(false)}
          onSubmit={(items) => handleSubmitSplitAmountsModal(items)}
          selectedReconcilables={newReconciliations.map((reconciliation) => ({
            ...reconciliation,
            originalAmountCents:
              reconciliation.creditCentsToReconcile + reconciliation.debitCentsToReconcile,
            updatedAmountCents:
              reconciliation.creditCentsToReconcile + reconciliation.debitCentsToReconcile,
          }))}
          totalAmountCents={initialAmountPendingToBeReconciled}
          totalAmountCurrency={source.currency}
          locale={locale}
        />
      )}
      {source && showPartialPendingBankJournalModal && (
        <PartialPendingJournalModal
          source={source}
          selectedReconciliations={selectedReconciliations}
          locale={locale}
          onClose={() => setShowPartialPendingBankJournalModal(false)}
          onConfirm={handleSubmitPartialModal}
          isBankJournalLine
        />
      )}

      {source && showPartialPendingNotBankJournalModal && (
        <PartialPendingJournalModal
          source={source}
          selectedReconciliations={selectedReconciliations}
          locale={locale}
          onClose={() => setShowPartialPendingNotBankJournalModal(false)}
          onConfirm={handleSubmitPartialModal}
          isBankJournalLine={false}
        />
      )}
    </Box>
  )
}
